// src/features/authn/ClientCreateDialog.tsx
// "+ New OAuth2 client" — desktop-app create dialog (docs/ux/14-forms.md)
// built on the form-kit (FormDialog/TitleInput/AutoGrowTextarea/SegmentedControl).
// SECURITY: the client secret is returned once by CreateOAuth2Client and shown
// once here — same one-time reveal semantics as the old routed create page.
// The dialog is marked dirty while the secret is on screen so a stray Esc or
// backdrop click cannot silently discard it.

import { useState } from 'react';
import { observer } from '@react';
import { useAuthn, useI18n } from '@react/hooks';
import { Alert, ErrorAlert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow, SegmentedControl } from '@/components/form';
import { UriListEditor } from './UriListEditor';

export interface ClientCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create (list refresh). */
  onCreated?: () => void;
}

const fieldClass =
  'w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring';

export const ClientCreateDialog = observer(({ open, onClose, onCreated }: ClientCreateDialogProps) => {
  const authnStore = useAuthn();
  const { t } = useI18n();

  const [clientName, setClientName] = useState('');
  const [clientType, setClientType] = useState('confidential');
  const [description, setDescription] = useState('');
  const [redirectUris, setRedirectUris] = useState<string[]>([]);
  const [grantTypes, setGrantTypes] = useState('authorization_code');
  const [scopes, setScopes] = useState('openid profile email');
  const [responseTypes, setResponseTypes] = useState('code');
  const [logoUri, setLogoUri] = useState('');
  const [policyUri, setPolicyUri] = useState('');
  const [tosUri, setTosUri] = useState('');
  const [contacts, setContacts] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [clientSecret, setClientSecret] = useState('');

  const dirty = !!(clientName.trim() || description.trim() || redirectUris.some((u) => u.trim()));

  const reset = () => {
    setClientName('');
    setClientType('confidential');
    setDescription('');
    setRedirectUris([]);
    setGrantTypes('authorization_code');
    setScopes('openid profile email');
    setResponseTypes('code');
    setLogoUri('');
    setPolicyUri('');
    setTosUri('');
    setContacts('');
    setShowMore(false);
    setSubmitError(null);
    setClientSecret('');
  };

  const finish = () => {
    reset();
    onCreated?.();
    onClose();
  };

  const submit = async () => {
    if (!clientName.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const resp = await authnStore.CreateOAuth2Client({
        entity: {} as never,
        client_name: clientName,
        client_type: clientType,
        description,
        redirect_uris: { items: redirectUris.map((s) => s.trim()).filter(Boolean) },
        allowed_grant_types: { items: grantTypes.split(',').map((s) => s.trim()).filter(Boolean) },
        allowed_scopes: { items: scopes.split(' ').filter(Boolean) },
        allowed_response_types: { items: responseTypes.split(',').map((s) => s.trim()).filter(Boolean) },
        logo_uri: logoUri,
        policy_uri: policyUri,
        tos_uri: tosUri,
        contacts: { items: contacts.split(',').map((s) => s.trim()).filter(Boolean) },
      });
      const secret = (resp as never as { client_secret?: string }).client_secret;
      if (secret) {
        // One-time reveal: keep the dialog open until the admin acknowledges.
        setClientSecret(secret);
      } else {
        finish();
      }
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  // One-time secret reveal step — shown once, never re-fetchable.
  if (clientSecret) {
    return (
      <FormDialog
        open={open}
        onClose={finish}
        onSubmit={finish}
        submitLabel={t('authn.backToClients')}
        dirty
        aria-label={t('authn.clientCreated')}
      >
        <div className="space-y-3 px-3.5 pb-4 pt-2">
          <h2 className="text-lg font-semibold text-foreground">{t('authn.clientCreated')}</h2>
          <Alert variant="warning">{t('authn.saveClientSecret')}</Alert>
          <div>
            <p className="mb-1 text-sm text-muted-foreground">{t('authn.clientSecret')}</p>
            <code className="block select-all break-all rounded-lg bg-muted p-3 font-mono text-sm">{clientSecret}</code>
          </div>
        </div>
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      onSubmit={submit}
      submitLabel={t('authn.createSubmit')}
      canSubmit={!!clientName.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      width="lg"
      aria-label={t('authn.create')}
      context={
        <SegmentedControl
          aria-label={t('authn.clientType')}
          size="sm"
          value={clientType}
          onChange={setClientType}
          options={[
            { value: 'confidential', label: 'Confidential' },
            { value: 'public', label: 'Public' },
          ]}
        />
      }
      footerLeft={
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          {showMore ? t('authn.fewerFields') : `+ ${t('authn.moreFields')}`}
        </button>
      }
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {!!submitError && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}

        <TitleInput
          value={clientName}
          onChange={setClientName}
          placeholder={t('authn.clientNamePlaceholder')}
          initialFocus
          onEnter={() => document.getElementById('authn-create-description')?.focus()}
          aria-label={t('authn.clientName')}
        />
        <AutoGrowTextarea
          id="authn-create-description"
          value={description}
          onChange={setDescription}
          placeholder={t('authn.descriptionPlaceholder')}
          minRows={1}
        />

        <FormRow label={t('authn.redirectUris')} hint={t('authn.redirectUrisHelper')} labelWidth="sm">
          <UriListEditor
            value={redirectUris}
            onChange={setRedirectUris}
            addLabel={t('authn.addRedirectUri')}
            emptyLabel={t('authn.noRedirectUris')}
            placeholder={t('authn.redirectUriPlaceholder')}
            removeLabel={t('common.remove')}
            idPrefix="authn-create-uri"
          />
        </FormRow>

        {showMore && (
          <div className="border-t border-border pt-1">
            <FormRow label={t('authn.grantTypes')} hint="authorization_code, client_credentials, refresh_token" labelWidth="sm" htmlFor="authn-create-grants">
              <input id="authn-create-grants" type="text" value={grantTypes} onChange={(e) => setGrantTypes(e.target.value)} autoCapitalize="off" className={fieldClass} />
            </FormRow>
            <FormRow label={t('authn.scopes')} hint="openid profile email" labelWidth="sm" htmlFor="authn-create-scopes">
              <input id="authn-create-scopes" type="text" value={scopes} onChange={(e) => setScopes(e.target.value)} autoCapitalize="off" className={fieldClass} />
            </FormRow>
            <FormRow label={t('authn.responseTypes')} labelWidth="sm" htmlFor="authn-create-responses">
              <input id="authn-create-responses" type="text" value={responseTypes} onChange={(e) => setResponseTypes(e.target.value)} autoCapitalize="off" className={fieldClass} />
            </FormRow>
            <FormRow label={t('authn.logoUri')} labelWidth="sm" htmlFor="authn-create-logo">
              <input id="authn-create-logo" type="text" value={logoUri} onChange={(e) => setLogoUri(e.target.value)} autoCapitalize="off" className={`${fieldClass} font-mono`} />
            </FormRow>
            <FormRow label={t('authn.policyUri')} labelWidth="sm" htmlFor="authn-create-policy">
              <input id="authn-create-policy" type="text" value={policyUri} onChange={(e) => setPolicyUri(e.target.value)} autoCapitalize="off" className={`${fieldClass} font-mono`} />
            </FormRow>
            <FormRow label={t('authn.tosUri')} labelWidth="sm" htmlFor="authn-create-tos">
              <input id="authn-create-tos" type="text" value={tosUri} onChange={(e) => setTosUri(e.target.value)} autoCapitalize="off" className={`${fieldClass} font-mono`} />
            </FormRow>
            <FormRow label={t('authn.contacts')} hint={t('authn.contactsHelper')} labelWidth="sm" htmlFor="authn-create-contacts">
              <input id="authn-create-contacts" type="text" value={contacts} onChange={(e) => setContacts(e.target.value)} autoCapitalize="off" className={fieldClass} />
            </FormRow>
          </div>
        )}
      </div>
    </FormDialog>
  );
});

export default ClientCreateDialog;
